import { Injectable } from '@angular/core';
import { catchError, Observable, tap } from 'rxjs';
import { LoginRequest } from '@app/@shared/models/login-request.model';
import { LoginResponse } from '../models/login-response.model';
import { ApplicationProfile, PortalTheme } from '../models/application-profile.model';
import { ChangePassword } from '../models/change-password.model';
import { ResetPasswordRequest } from '../models/reset-password-request.model';
import { Credentials } from '../models/credentials.model';
import { Resources } from '../models/constants';
import { Representative } from '../models/representative.model';
import { ApiResponse } from '../models/api-response.model';
import { LoginTypes } from '../enums/login-types.enum';
import { CreateUpdatePortalThemeRequest } from '../models/create-update-portal-theme-request.model';
import { BaseApiService } from '@ep/shared';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BaseApiService {
  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    const json = JSON.stringify(loginRequest);
    return this.post<LoginResponse>('/v2/Security/Login', json);
  }

  logout(): Observable<void> {
    return this.get<void>('/v2/Security/Logout', undefined).pipe(
      tap(() => {
        const savedCredentials = localStorage.getItem(Resources.CredentialsKey);
        if (savedCredentials) {
          const credentials: Credentials = JSON.parse(savedCredentials);

          credentials.jwt = null;
          const updatedCredentialsJSON = JSON.stringify(credentials);

          localStorage.setItem(Resources.CredentialsKey, updatedCredentialsJSON);
        }
      })
    );
  }

  forgotPassword(forgotPasswordRequest: ResetPasswordRequest): Observable<void> {
    const json = JSON.stringify(forgotPasswordRequest);
    return this.post<void>('/v2/Security/ForgotPassword', json);
  }

  getApplicationProfile(): Observable<ApplicationProfile> {
    return this.get<ApplicationProfile>('/v2/ApplicationProfile/representative').pipe(
      catchError((err) => {
        console.error('Issue with getting employee profile');
        throw err;
      }),
      map(mapToApplicationProfile)
    );
  }

  changePassword(changePassword: ChangePassword): Observable<void> {
    const json = JSON.stringify(changePassword);
    return this.post<void>('/v2/Security/ChangePassword', json);
  }

  confirmPasswordReset(confirmPasswordReset: ChangePassword): Observable<void> {
    const json = JSON.stringify(confirmPasswordReset);
    return this.post<void>('/v2/Security/ConfirmPasswordReset', json);
  }

  getRepresentativeByPasswordResetId(passwordResetId: string): Observable<ApiResponse<Representative>> {
    const queryParams = {
      loginType: LoginTypes.Representative,
    };

    return this.get<{ $values?: any[] }>(`/v2/Security/PasswordReset/${passwordResetId}`, queryParams).pipe(
      map((response) => {
        const collection = (response?.$values ?? []).map(mapToRepresentative);
        return {
          Collection: collection,
          PageNumber: 0,
          DisplayCount: collection.length,
          TotalRecordCount: collection.length,
          ErrorCode: 0,
          ErrorMessage: [],
          ErrorMessageCodes: [],
          InvalidDatas: [],
          ResponseType: 0,
          ResponseMessages: [],
          Errors: null,
        } as ApiResponse<Representative>;
      })
    );
  }

  updateMerchantPortalTheme(coalitionId: number, themeRequest: CreateUpdatePortalThemeRequest): Observable<void> {
    const json = JSON.stringify(themeRequest);
    return this.post<void>(`/v2/ApplicationProfile/portal-theme/${coalitionId}`, json);
  }
}

function mapToRepresentative(representative: any): Representative {
  return {
    $id: representative.$id,
    RepresentativeId: representative.representativeId,
    CoalitionId: representative.coalitionId ?? 0,
    NameFirst: representative.nameFirst,
    NameLast: representative.nameLast,
    EmailAddress: representative.emailAddress,
    PhoneNumber: representative.phoneNumber,
    AccessType: representative.accessType,
    LanguageType: representative.languageType,
    RepresentativeType: representative.representativeType,
  };
}

function mapToApplicationProfile(profile: any): ApplicationProfile {
  return {
    Representative: {
      $id: profile.representative.$id,
      RepresentativeId: profile.representative.representativeId,
      CoalitionId: profile.representative.coalitionId,
      NameFirst: profile.representative.nameFirst,
      NameLast: profile.representative.nameLast,
      EmailAddress: profile.representative.emailAddress,
      PhoneNumber: profile.representative.phoneNumber,
      AccessType: profile.representative.accessType,
      StatusType: profile.representative.statusType,
      Coalition: {
        $id: profile.representative.coalition.$id,
        CoalitionId: profile.representative.coalition.coalitionId,
        Name: profile.representative.coalition.name,
        InternalPortalDomain: profile.representative.coalition.internalPortalDomain,
        MerchantPortalDomain: profile.representative.coalition.merchantPortalDomain,
      },
      PortalTheme: {
        Theme: mapToApplicationProfilePortalTheme(profile?.portalTheme?.theme),
        PrimaryLogo: profile?.portalTheme?.primaryLogo,
        SecondaryLogo: profile?.portalTheme?.secondaryLogo,
        FavIcon: profile?.portalTheme?.favIcon,
        PendingPrimaryLogo: profile?.portalTheme?.pendingPrimaryLogo,
        PendingSecondaryLogo: profile?.portalTheme?.pendingSecondaryLogo,
        PendingFavIcon: profile?.portalTheme?.pendingFavIcon,
      },
    },
  };

  function mapToApplicationProfilePortalTheme(value: string | any): PortalTheme | undefined {
    if (!value) {
      return undefined;
    }

    const themeData = typeof value === 'string' ? JSON.parse(value) : value;

    return {
      primary: themeData.primary,
      backgroundColor: themeData.backgroundColor,
      defaultTextColor: themeData.defaultTextColor,
      sidebar: {
        backgroundColor: {
          primary: themeData.sidebar.backgroundColor.primary,
          secondary: themeData.sidebar.backgroundColor.secondary,
          active: themeData.sidebar.backgroundColor.active,
          dropdown: themeData.sidebar.backgroundColor.dropdown,
        },
        textColor: themeData.sidebar.textColor,
        logoBackgroundColor: themeData.sidebar.logoBackgroundColor,
      },
      header: {
        primary: themeData.header.primary,
        backgroundColor: themeData.header.backgroundColor,
        itemsColor: themeData.header.itemsColor,
        textColor: themeData.header.textColor,
      },
      dashboard: {
        coloredCardTextColor: themeData.dashboard.coloredCardTextColor,
        sales: themeData.dashboard.sales,
        loyalty: themeData.dashboard.loyalty,
        rewards: themeData.dashboard.rewards,
        reviews: themeData.dashboard.reviews,
        cardBackgroundColor: themeData.dashboard.cardBackgroundColor,
        headerBackgroundColor: themeData.dashboard.headerBackgroundColor,
        headerTextColor: themeData.dashboard.headerTextColor,
      },
      table: {
        header: {
          backgroundColor: themeData.table.header.backgroundColor,
          textColor: themeData.table.header.textColor,
        },
        subHeaderBackgroundColor: themeData.table.subHeaderBackgroundColor,
        subHeaderTextColor: themeData.table.subHeaderTextColor,
        statusColors: {
          primary: themeData.table.statusColors.primary,
          secondary: themeData.table.statusColors.secondary,
          warning: themeData.table.statusColors.warning,
          danger: themeData.table.statusColors.danger,
        },
        body: {
          primaryBackgroundColor: themeData.table.body.primaryBackgroundColor,
          secondaryBackgroundColor: themeData.table.body.secondaryBackgroundColor,
          textColor: themeData.table.body.textColor,
        },
      },
      form: {
        header: {
          primaryBackgroundColor: themeData.form.header.primaryBackgroundColor,
          secondaryBackgroundColor: themeData.form.header.secondaryBackgroundColor,
          textColor: themeData.form.header.textColor,
          itemsColor: themeData.form.header.itemsColor,
          itemsTextColor: themeData.form.header.itemsTextColor,
        },
        backgroundColor: themeData.form.backgroundColor,
      },
    };
  }
}
